/**
 * External dependencies
 */
import { registerPaymentMethod } from '@woocommerce/blocks-registry';
import { __ } from '@wordpress/i18n';
import { decodeEntities } from '@wordpress/html-entities';
import { getSetting } from '@woocommerce/settings';
import { useEffect } from '@wordpress/element';

/**
 * Internal dependencies
 */
import './index.scss';

const settings = getSetting( 'iotec_pay_data', {} );

/**
 * Content component
 */
const Content = ( props ) => {
	const { description, phone_number_label } = settings;
	const { eventRegistration, emitResponse } = props;
	const { onPaymentProcessing } = eventRegistration;

	useEffect( () => {
		const unsubscribe = onPaymentProcessing( async () => {
			const phoneNumber = document.getElementById( 'iotec_mm_no' )?.value;

			if ( ! phoneNumber ) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: __( 'Mobile Money number is required.', 'iotec' ),
				};
			}

			if ( ! isPhoneNumberValid( phoneNumber ) ) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: __(
						'Please enter a valid Mobile Money number (Format: 256700000000)',
						'iotec'
					),
				};
			}

			return {
				type: emitResponse.responseTypes.SUCCESS,
				meta: {
					paymentMethodData: {
						iotec_mm_no: phoneNumber,
					},
				},
				messageContext: emitResponse.noticeContexts.PAYMENTS,
			};
		} );

		return () => {
			unsubscribe();
		};
	}, [
		emitResponse.responseTypes.ERROR,
		emitResponse.responseTypes.SUCCESS,
		emitResponse.noticeContexts.PAYMENTS,
		onPaymentProcessing,
	] );

	return (
		<>
			{ hasValue( description ) && (
				<p>{ decodeEntities( description ) }</p>
			) }
			<div className="iotec-pay-form">
				<label htmlFor="iotec_mm_no">
					{ phone_number_label ||
						__( 'Mobile Money Number*', 'iotec' ) }
				</label>
				<input
					type="tel"
					id="iotec_mm_no"
					name="iotec_mm_no"
					required
					placeholder="256700000000"
					style={ {
						flex: 1,
						border: '1px solid #ddd',
						borderRadius: '4px',
					} }
				/>
			</div>
		</>
	);
};

/**
 * Label component
 */
const Label = () => {
	const label = decodeEntities( settings.title || '' );
	const [ logo ] = settings.logo_urls;
	return (
		<>
			{ logo && (
				<img src={ logo } alt={ label } className="iotec-pay-logo" />
			) }
			<span>&nbsp;{ label }</span>
		</>
	);
};

const isPhoneNumberValid = ( phoneNumber ) => {
	if ( ! hasValue( phoneNumber ) ) {
		return false;
	}
	return phoneNumber.match( /^(256|0)[0-9]{9}$/ );
};

const hasValue = ( value ) => {
	if (
		! value ||
		( typeof value === 'string' && value.trim().length === 0 )
	) {
		return false;
	}
	return true;
};

/**
 * Register payment method
 */
const IotecPayPaymentMethod = {
	name: 'iotec_pay',
	label: <Label />,
	content: <Content />,
	edit: <Content />,
	canMakePayment: () => true,
	ariaLabel: decodeEntities( settings.title || '' ),
	supports: {
		features: settings.supports || [],
	},
	paymentMethodId: 'iotec_pay',
	billing: {
		phone: {
			required: true,
		},
	},
};

registerPaymentMethod( IotecPayPaymentMethod );
